/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import jwt_decode from "jwt-decode";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  FlexContainer,
  Input,
  Label,
  Message,
  Spacer,
  Text,
  theme,
} from "@nordcloud/gnui";
import { ValidationError } from "~/generated/graphql";
import { ExternalLink, FormGroup, FormWrap } from "~/components";
import { isNotEmpty } from "~/tools";
import { StepContainer } from "~/views/accounts/AddAccount/components/StepContainer";

enum FormField {
  ENROLLMENT_NUMBER = "enrollment_number",
  ENROLLMENT_ACCESS_KEY = "enrollment_access_key",
}

type FormData = {
  [FormField.ENROLLMENT_NUMBER]: string;
  [FormField.ENROLLMENT_ACCESS_KEY]: string;
};

type Props = {
  onSubmit: SubmitHandler<FormData>;
  onPrevStep: () => void;
  validationErrors?: ValidationError[];
};

type AzureJwtToken = {
  accountId?: string;
  departmentId?: string;
  enrollmentNumber?: string;
  id?: string;
  partnerId?: string;
  reportView?: string;
  aud: string;
  exp: number;
  iss: string;
  nbf?: number;
};

function validateAzureJwt(token: string) {
  const isJwtTokenQuery =
    "^([a-zA-Z0-9_=]+)\\.([a-zA-Z0-9_=]+)\\.([a-zA-Z0-9_\\-+\\/=]*)";

  if (!new RegExp(isJwtTokenQuery).test(token)) {
    return "Not valid token.";
  }

  const decoded: AzureJwtToken = jwt_decode(token);
  const { iss, aud, exp } = decoded;

  if (iss !== "ea.microsoftazure.com") {
    return "Token is not valid, wrong issuer field.";
  }

  if (aud !== "client.ea.microsoftazure.com") {
    return "Token is not valid, wrong audience field.";
  }

  if (dayjs.unix(exp).diff(dayjs()) < 0) {
    return "Token is expired.";
  }

  if (dayjs.unix(exp).subtract(48, "hours").diff(dayjs()) < 0) {
    return "Token will expire in less than 48 hours.";
  }

  return true;
}

export function AzureEAForm({
  onSubmit,
  onPrevStep,
  validationErrors = [],
}: Props) {
  const {
    register,
    handleSubmit,

    formState: { isValid, errors },
  } = useForm<FormData>({ mode: "onChange" });

  const disableSubmit = !isValid || isNotEmpty(Object.keys(errors));

  return (
    <FormWrap
      footerProps={{
        nextButtonProps: { disabled: disableSubmit },
        onPrevClick: onPrevStep,
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <FlexContainer wrap="wrap" direction="column" alignItems="stretch">
        <Text weight="medium">Account Credentials</Text>
        <Text size="md" color={theme.color.text.text02}>
          To on-board a subscription you need to create a Service Principal.
        </Text>
        <Spacer height={theme.spacing.spacing02} />
        <StepContainer counter={1}>
          <Text>
            Log in to:{" "}
            <ExternalLink href="https://ea.azure.com">
              https://ea.azure.com
            </ExternalLink>
          </Text>
        </StepContainer>
        <StepContainer counter={2}>
          <Text>
            In menu, select <i>Management</i> to view your enrollment number.
            <br />
            Record this number for later use.
          </Text>
        </StepContainer>
        <StepContainer counter={3}>
          <Text>
            In menu, select <i>Reports</i>.<br />
            From the <i>Download Usage</i> tab, select <i>API Access Key</i>.
            <br />
            Record this key for later use.
          </Text>
        </StepContainer>
        <StepContainer counter={4}>
          <Text>Add this information to access your billing data:</Text>
          {validationErrors
            .filter(({ field }) => field === null || field === "")
            .map(({ error }) => (
              <Message key={error} status="danger" image="danger">
                {error}
              </Message>
            ))}
          <Spacer height={theme.spacing.spacing04} />
          <div css={{ width: "100%" }}>
            <FormGroup error={errors[FormField.ENROLLMENT_NUMBER]}>
              <Label
                required
                htmlFor={FormField.ENROLLMENT_NUMBER}
                name="Enrollment number"
              />
              <Input
                id={FormField.ENROLLMENT_NUMBER}
                {...register(FormField.ENROLLMENT_NUMBER, {
                  required: "This field is required.",
                  pattern: {
                    value: /\d{7,8}/,
                    message: "Enter a 7 or 8-digit number.",
                  },
                  minLength: {
                    value: 7,
                    message: "Minimum 7 characters required.",
                  },
                  maxLength: {
                    value: 8,
                    message: "Maximum 8 characters allowed.",
                  },
                })}
              />
            </FormGroup>
            <FormGroup error={errors[FormField.ENROLLMENT_ACCESS_KEY]}>
              <Label
                required
                htmlFor={FormField.ENROLLMENT_ACCESS_KEY}
                name="Enrollment Access Key"
              />
              <Input
                id={FormField.ENROLLMENT_ACCESS_KEY}
                {...register(FormField.ENROLLMENT_ACCESS_KEY, {
                  required: "This field is required.",
                  validate: validateAzureJwt,
                })}
              />
            </FormGroup>
          </div>
        </StepContainer>
      </FlexContainer>
    </FormWrap>
  );
}
